import React, { memo, useState, useEffect, useRef } from 'react';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const SearchInput = memo<SearchInputProps>(({ value, onChange, placeholder }) => {
  const [localValue, setLocalValue] = useState(value);
  const debounceTimeoutRef = useRef<NodeJS.Timeout>();

  // Update local value when prop changes (e.g., when cleared externally)
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (newValue: string) => {
    // Update local state immediately - no delay for typing
    setLocalValue(newValue);
    
    // Clear existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    // Debounce the callback to parent
    debounceTimeoutRef.current = setTimeout(() => {
      onChange(newValue);
    }, 300); // 300ms delay
  };

  const handleClear = () => {
    setLocalValue('');
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    onChange(''); // Clear immediately, no debounce
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="relative mb-4">
      <input
        type="text"
        placeholder={placeholder || "Search contacts by name, email, or company..."}
        value={localValue}
        onChange={(e) => handleChange(e.target.value)}
        className="w-full px-4 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />
      {localValue && (
        <button
          onClick={handleClear}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      )}
    </div>
  );
});

SearchInput.displayName = 'SearchInput';

export default SearchInput;
